import {Autocomplete, FormControl, TextField} from '@mui/material'
import React, {SyntheticEvent, useMemo} from 'react'
import {Label} from '../../../../../genericComponents/typographie/Label'
import {ServiceRequestStep} from '../../../genericComponents/formTemplate/steps/ServiceRequestStep'
import {useFormikContext} from 'formik'
import {ItemCreationForm} from '../../../../model/NewItemInput'
import {useLazySearchOrganizationsQuery} from '../../../../../organization/rtkOrganizationApi'
import './CompleteManufacturerItemCreationForm.scss'
import {StepSection} from './StepSection'
import CustomSwitch from '../../../../../genericComponents/customSwitch/CustomSwitch'
import {ItemOrganization} from '../../../../../item/model/ItemOrganization'
import {CustomTooltip} from '../../../../../genericComponents/customTooltip/CustomTooltip'
import InfoIcon from '@mui/icons-material/Info'
import {ClassificationTypeEnum} from '../../classificationNode/ClassificationNode'
import {AutoCompleteClassificationNode} from '../../common/AutoCompleteClassificationNode'

export const CompleteManufacturerItemCreationForm = () => {

    const formik = useFormikContext<ItemCreationForm>()

    const [trigger, result] = useLazySearchOrganizationsQuery()

    const searchOrganization = (event: SyntheticEvent<Element, Event>, value: string) => {
        if (value.length >= 3) {
            trigger({searchString: value})
        }
    }

    const organizations: ItemOrganization[] = useMemo(() => {
        if (!result.data) return []

        return result.data.map(org => {
            return {id: org?.id, label: org?.label, codificationSystem: org?.codificationSystem, code: org?.code}
        }).sort((a, b) => a.label.localeCompare(b.label))
    }, [result])

    return <ServiceRequestStep>
        <StepSection title="Identification">
            <FormControl>
                <Label htmlFor="part-number" required>
                    Part Number
                </Label>
                <TextField
                    data-testId = "part-number"
                    required fullWidth name="partNumber"
                    id="part-number"
                    placeholder="Input Part Number"
                    value={formik.values.partNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.partNumber && Boolean(formik.errors.partNumber)}
                    inputProps={{'data-cy': 'partNumber-form'}}
                />
            </FormControl>

            <FormControl>
                <Label htmlFor="manufacturer" required>
                    Manufacturer
                </Label>
                <Autocomplete
                    id="manufacturer"
                    data-cy="organization-identification-form"
                    data-test={formik.values.definedByOrganization?.id}
                    fullWidth
                    value={formik.values.definedByOrganization ?? null}
                    options={organizations}
                    classes={{paper: 'select-option'}}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={option => option.label}
                    onBlur={() => formik.setFieldTouched('definedByOrganization', true)}
                    onChange={(_, value) => value && formik.setFieldValue('definedByOrganization', value)}
                    onInputChange={searchOrganization}
                    noOptionsText={formik.values.organizationInput?.length >= 3 ? 'No options' : 'Please, input at least 3 characters'}
                    renderOption={(props, option) =>
                        <li {...props} key={option.id} data-cy={option.id}> {option.label}</li>}
                    renderInput={(params) =>
                        <TextField {...params}
                                   data-testId = "manufacturer"
                                   required
                                   data-cy="organization-identification-form-input"
                                   placeholder="Select Manufacturer"
                                   error={Boolean(formik.errors.definedByOrganization) && formik.touched.definedByOrganization}
                        />}
                />
            </FormControl>

            <FormControl>
                <Label htmlFor="technicalClassification" required>
                    Technical Classification
                </Label>
            <AutoCompleteClassificationNode
                classificationNodeIdToSearch={ClassificationTypeEnum.TECHNICAL}
                formikClassificationNodeFieldName = "technicalClassificationNode"/>
            </FormControl>

            <FormControl>
                <Label htmlFor="underNDA" required className= "underNDA-form">
                    <span className="label-with-tooltip">
                            Under Non Disclosure Agreement
                        <CustomTooltip
                        title={'NDA is a legally binding contract that establishes a confidential relationship between two parties.'}
                        placement="right-end"
                        arrow>
                        <InfoIcon className = "info-icon"/>
                        </CustomTooltip>
                    </span>
                </Label>
                <CustomSwitch
                    value="under-NDA"
                    toggleChecked={(e) => formik.setFieldValue('underNDA', e.target.checked)}
                    disabled={false}
                    dataTestid="under-NDA"
                    isChecked={formik.values.underNDA}
                    checkedLabel="Yes"
                    uncheckedLabel="No"
                />
            </FormControl>
        </StepSection>
    </ServiceRequestStep>
}
